<template>
  <div id="calendar-qr-tab" class="min-h-[220px]">
    <div v-if="loadFailed" class="flex min-h-[200px] flex-col items-center justify-center text-center">
      <p class="text-sm font-medium text-gray-700">
        {{ t('settings.calendar.qr_code_load_error') }}
      </p>
    </div>
    <div v-else-if="!QrPanel" class="flex min-h-[200px] flex-col items-center justify-center text-center text-sm text-gray-500">
      <UISpin size="small" />
      <p class="mt-2">{{ t('settings.calendar.loading') }}</p>
    </div>
    <component
      :is="QrPanel"
      v-else
      surface="panel"
      editor-teleport-to="#calendar-qr-tab"
      :emitter="emitter"
      :locale="locale"
      :show="true"
      :template-data="templateData"
      :location-id="locationId"
      template-types="CALENDAR"
      product="calendars"
      :store="storeShim"
      :user="user"
    />
  </div>
</template>

<script setup lang="ts">
  import { defineAsyncMicroApp } from '@gohighlevel/ghl-module-federation-plugin'
  import { UISpin } from '@gohighlevel/ghl-ui'
  import type { Component, PropType } from 'vue'
  import { computed, markRaw, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import AppState from '../../../states/app'
  import type { ICalendarShare } from '../../../types'
  import { copyToClipboard } from '../../../utils/generalFunctions'
  import { qrSourceTypeFor } from './qrSource'

  /*
    Renders the QR builder's panel as the share modal's QR tab.

    The remote is `qrCodeBuilderApp`, exposed as `./qr-integration` and
    switched to the embedded layout with `surface="panel"`. It owns
    provisioning, render, copy, download and the editor; everything here is
    host wiring.
  */
  const props = defineProps({
    data: {
      type: Object as PropType<ICalendarShare>,
      default: null,
    },
  })

  const { t, locale: i18nLocale } = useI18n()

  const QrPanel = shallowRef<Component | null>(null)
  const loadFailed = ref(false)

  const locationId = computed(() => AppState.locationId || '')
  const locale = computed(() => AppState.locale || i18nLocale.value || 'en-US')
  const user = computed(() => AppState.user ?? {})

  /*
    The QR remote authenticates through `window.getToken()`, which its host is
    expected to define — spm-ts does. Inside this iframe the token lives on
    AppState and is refreshed by the parent on 401, so bridge the two.
  */
  function installTokenBridge() {
    const w = window as unknown as { getToken?: () => Promise<string> }
    if (!w.getToken) {
      w.getToken = async () => AppState.tokenId
    }
  }

  // mitt-style emitter the remote expects. mitt is not a dependency here.
  function createEmitter() {
    const handlers = new Map<string, Array<(payload?: unknown) => void>>()
    return {
      on(event: string, handler: (payload?: unknown) => void) {
        handlers.set(event, [...(handlers.get(event) ?? []), handler])
      },
      off(event: string, handler: (payload?: unknown) => void) {
        handlers.set(
          event,
          (handlers.get(event) ?? []).filter(h => h !== handler)
        )
      },
      emit(event: string, payload?: unknown) {
        for (const handler of handlers.get(event) ?? []) handler(payload)
      },
    }
  }
  const emitter = createEmitter()

  // The shell owns the picker and sits outside this iframe: the request goes over
  // Postmate, the choice comes back on AppState. The marker keeps other pickers
  // in this app from landing in the QR editor.
  const QR_MEDIA_FIELD = 'qrCodeBuilder'

  function onMediaLibraryOpen(settings?: unknown) {
    AppState.mediaLibraryFieldType = QR_MEDIA_FIELD
    AppState.parentApp?.then((parent: { emit: (event: string, payload: unknown) => void }) => {
      // tokenId is the one setting the remote cannot know.
      parent.emit('media-library-open', { ...(settings as Record<string, unknown>), tokenId: AppState.tokenId })
      // setMediaFileURI drops the result unless this is set.
      AppState.isMediaLibraryOpen = true
    })
  }

  // Sync: the meetings page watches the same field and clears it first, so on
  // the default queue this callback would never run.
  const stopMediaWatch = watch(
    () => AppState.mediaFileURI,
    url => {
      if (!url || AppState.mediaLibraryFieldType !== QR_MEDIA_FIELD) return
      emitter.emit('mediaSelected', { url })
      AppState.mediaLibraryFieldType = ''
      AppState.mediaFileURI = ''
    },
    { flush: 'sync' }
  )

  // The clipboard is blocked inside this iframe, so the shell copies for us —
  // the same route this app's own copy buttons take.
  function onCopyToClipboard(link?: unknown) {
    copyToClipboard(link as string)
  }

  // Postmate's types leave `model` off ChildAPI, though it is there at runtime.
  const modelOf = (parent: unknown) => (parent as { model?: Record<string, unknown> }).model

  function onManageBrandColors() {
    AppState.parentApp?.then(parent => {
      parent.emit('open-global-settings')
    })
  }

  function onGlobalSettingsClosed() {
    emitter.emit('global-settings-closed')
  }

  AppState.parentApp
    ?.then(parent => {
      const model = modelOf(parent)
      if (model) model.globalSettingsClosed = onGlobalSettingsClosed
    })
    .catch(e => console.error('[QrCode] failed to register globalSettingsClosed', e))

  emitter.on('media-library-open', onMediaLibraryOpen)
  emitter.on('copy-to-clipboard', onCopyToClipboard)
  emitter.on('ext-route-to-brand-boards', onManageBrandColors)

  onBeforeUnmount(() => {
    emitter.off('media-library-open', onMediaLibraryOpen)
    emitter.off('copy-to-clipboard', onCopyToClipboard)
    emitter.off('ext-route-to-brand-boards', onManageBrandColors)
    AppState.parentApp
      ?.then(parent => {
        const model = modelOf(parent)
        if (model?.globalSettingsClosed === onGlobalSettingsClosed) delete model.globalSettingsClosed
      })
      .catch(e => console.error('[QrCode] failed to unregister globalSettingsClosed', e))
    stopMediaWatch()
  })

  const templateData = computed(() => ({
    id: props.data?.id,
    name: props.data?.name,
    sourceType: qrSourceTypeFor(props.data?.source),
  }))

  /*
    Shape the QR editor reads off its host store. Only the editor consumes it —
    the panel takes locationId as a prop — so this exists to keep "Edit QR" from
    throwing rather than to be complete.
  */
  const storeShim = computed(() => ({
    state: {
      locations: {
        locations: [
          {
            id: locationId.value,
            companyId: AppState.companyId,
            domain: AppState.whitelabelDomain,
          },
        ],
      },
    },
    getters: {
      'company/get': {
        id: AppState.companyId,
        domain: AppState.whitelabelDomain,
      },
    },
  }))

  const LOAD_TIMEOUT_MS = 15000

  async function loadPanel() {
    loadFailed.value = false
    QrPanel.value = null

    let timer: ReturnType<typeof setTimeout> | undefined
    try {
      const [Panel] = await Promise.race([
        defineAsyncMicroApp('qrCodeBuilderApp', ['qr-integration']),
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => reject(new Error('timed out loading qrCodeBuilderApp')), LOAD_TIMEOUT_MS)
        }),
      ])
      if (!Panel) throw new Error('qrCodeBuilderApp did not provide qr-integration')
      // markRaw: a component definition must not be turned into a reactive proxy.
      QrPanel.value = markRaw(Panel)
    } catch (e) {
      loadFailed.value = true
      console.error('[QrCode] failed to load qrCodeBuilderApp', e)
    } finally {
      if (timer) clearTimeout(timer)
    }
  }

  onMounted(() => {
    installTokenBridge()
    loadPanel()
  })
</script>
